import {
  LABEL_LIMIT,
  PASSWORD_LIMIT,
  USERNAME_LIMIT,
  type ConnectionScope,
  type WebsiteLogin,
} from "@typedoc/contracts/connections";
import { VALIDATION_ISSUE_CATALOG } from "@typedoc/contracts/errors";
import { parseSiteAddress } from "@typedoc/contracts/site-preview";
import type { RegionalSession } from "../regional-request";
import { connectionsRequest, membersPersonalQuery, type MembersPersonalOption } from "./api";

/**
 * Website logins (spec §10, decisions 29–31): a site, a username and a password, saved by the
 * regional API, which seals the password the moment it arrives. The browser sends it once, on
 * save or replace, and no answer ever carries it back.
 */
export { LABEL_LIMIT, PASSWORD_LIMIT, USERNAME_LIMIT };
export type { WebsiteLogin };

export type WebsiteLoginField = "address" | "username" | "password" | "label";

/**
 * The page to sign in on, from whatever was typed. The contract's parser, so the browser's hint
 * and the server's refusal follow one rule: https on the default port, a real host, no
 * credentials in the address.
 */
export const parseWebsiteAddress = parseSiteAddress;

export const ADDRESS_COPY = {
  invalid: "Enter the site's address, like portal.example.com.",
  insecure: "Use the site's https address. A password is never typed into an unencrypted page.",
} as const;

/** What is wrong with each field, in the shared catalog's words where it has them. */
export function websiteLoginErrors(input: {
  address: string;
  username: string;
  password: string;
  label: string;
}): Partial<Record<WebsiteLoginField, string>> {
  const errors: Partial<Record<WebsiteLoginField, string>> = {};
  if (!input.address.trim()) errors.address = VALIDATION_ISSUE_CATALOG.REQUIRED;
  else {
    const address = parseWebsiteAddress(input.address);
    if (!address.ok) errors.address = ADDRESS_COPY[address.reason];
  }
  if (!input.username.trim()) errors.username = VALIDATION_ISSUE_CATALOG.REQUIRED;
  else if (input.username.trim().length > USERNAME_LIMIT) {
    errors.username = VALIDATION_ISSUE_CATALOG.TOO_LONG;
  }
  const passwordError = passwordProblem(input.password);
  if (passwordError) errors.password = passwordError;
  if (input.label.trim().length > LABEL_LIMIT) errors.label = VALIDATION_ISSUE_CATALOG.TOO_LONG;
  return errors;
}

/** A password is kept exactly as typed, spaces included, so it is only checked for presence. */
export function passwordProblem(password: string): string | undefined {
  if (!password) return VALIDATION_ISSUE_CATALOG.REQUIRED;
  if (password.length > PASSWORD_LIMIT) return VALIDATION_ISSUE_CATALOG.TOO_LONG;
  return undefined;
}

export const fetchWebsiteLogins = (session: RegionalSession, options?: MembersPersonalOption) =>
  connectionsRequest(session, (c, workspaceId, call) =>
    c.websiteLogins({ params: { workspaceId }, query: membersPersonalQuery(options) }, call),
  );

export const createWebsiteLogin = (
  session: RegionalSession,
  input: {
    url: string;
    username: string;
    password: string;
    label: string;
    scope: ConnectionScope;
  },
) =>
  connectionsRequest(session, (c, workspaceId, call) =>
    c.createWebsiteLogin({ params: { workspaceId }, body: input }, call),
  );

export const replaceWebsitePassword = (
  session: RegionalSession,
  connectionId: string,
  body: { password: string; revision: number },
) =>
  connectionsRequest(session, (c, workspaceId, call) =>
    c.replaceWebsitePassword({ params: { workspaceId, connectionId }, body }, call),
  );

export const removeWebsiteLogin = (session: RegionalSession, connectionId: string) =>
  connectionsRequest(session, (c, workspaceId, call) =>
    c.removeWebsiteLogin({ params: { workspaceId, connectionId } }, call),
  );
